import React from 'react'
import type { CMSSectionRichContentCarousel } from '@/types/sanity'

import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/scss'
import 'swiper/scss/pagination'
import 'swiper/css/free-mode'
import 'swiper/scss/navigation'
import 'swiper/css/thumbs'
import './SectionRichContentCarousel.scss'
import { Section } from './Section'

import config from 'tailwind.config'

import { Link } from '@/components/Link/Link'
import { ResponsiveImage } from '@components/Shared/ResponsiveImage'
import { Video } from '@/components/Shared/Video'
import useMediaQuery from '@/hooks/useMediaQuery'
import { LinkButton } from '@/components/Link/LinkButton'

type Props = {
  content: CMSSectionRichContentCarousel
}

export const SectionRichContentCarousel: React.FC<Props> = ({ content }) => {
  const { _id, eyebrow, header, cards, theme, moduleCta } = content

  const isDesktopScreen = useMediaQuery(
    `(min-width: ${config.theme.screens.md})`,
  )

  return (
    <Section id={_id} theme={theme}>
      {cards.length > 4 && (
        <>
          <div className="section-rich-content-carousel__prev-button">
            <button
              className={`button button--transparentArrow`}
              type="button"
            ></button>
          </div>
          <div className="section-rich-content-carousel__next-button">
            <button
              className={`button button--transparentArrow`}
              type="button"
            ></button>
          </div>
        </>
      )}
      <div className="section-rich-content-carousel">
        {(eyebrow || header || moduleCta) && (
          <div className="carousel-header">
            {(eyebrow || header) && (
              <div className="carousel-header-text">
                {eyebrow && <p className="type-label">{eyebrow}</p>}
                {header && <h2 className="type-h3 md:type-h5">{header}</h2>}
              </div>
            )}
            {moduleCta && (
              <LinkButton
                link={moduleCta}
                className="module-cta type-label link__no-margin"
              />
            )}
          </div>
        )}

        <div className="carousel-container">
          <Swiper
            modules={[Navigation]}
            slidesPerView={isDesktopScreen ? 4 : 1.5}
            spaceBetween={16}
            breakpoints={{
              768: {
                spaceBetween: 32,
              },
            }}
            loop={isDesktopScreen ? cards.length > 4 : true}
            navigation={{
              prevEl: `[data-section-id="${_id}"] .section-rich-content-carousel__prev-button`,
              nextEl: `[data-section-id="${_id}"] .section-rich-content-carousel__next-button`,
            }}
            touchEventsTarget="container"
          >
            {cards.map((card, index) => (
              <SwiperSlide key={index}>
                <div className="carousel-card">
                  <div className={`card-body theme-${card.theme}`}>
                    {card.cardType === 'richTextCard' && (
                      <div className="card-rich-text">
                        <div className="card-rich-text__heading">
                          {card.richTextCardEyebrow && (
                            <p className="type-label">
                              {card.richTextCardEyebrow}
                            </p>
                          )}
                          {card.richTextCardHeading && (
                            <h4
                              className={`type-${card.richTextCardHeadingType}`}
                            >
                              {card.richTextCardHeading}
                            </h4>
                          )}
                          {card.richTextCardDescription && (
                            <p className="type-body">
                              {card.richTextCardDescription}
                            </p>
                          )}
                        </div>

                        <div className="card-rich-text__media-wrapper">
                          {card.richTextCardMedia && (
                            <div className="card-media">
                              {card.richTextCardMedia._type ===
                                'component.responsiveImage' && (
                                <ResponsiveImage
                                  document={card.richTextCardMedia}
                                />
                              )}
                              {card.richTextCardMedia._type ===
                                'component.video' && (
                                <Video
                                  videoUrl={
                                    card.richTextCardMedia.video.secureUrl
                                  }
                                  videoType={card.richTextCardMedia.videoType}
                                  loopVideo={
                                    card.richTextCardMedia.background?.loopVideo
                                  }
                                  dialogueThumbnail={
                                    card.richTextCardMedia.dialogue?.thumbnail
                                  }
                                  includeControls={
                                    card.richTextCardMedia.dialogue
                                      ?.includeControls
                                  }
                                  backupImage={
                                    card.richTextCardMedia.backupImage
                                  }
                                  buttonType="primary"
                                  ratios={card.richTextCardMedia.ratios}
                                  classes="rich-text-video"
                                />
                              )}
                            </div>
                          )}
                          {card.richTextCardCTA && (
                            <Link
                              link={card.richTextCardCTA}
                              classes={['link link__no-margin type-label']}
                            >
                              {card.richTextCardCTA.text}
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                    {card.cardType === 'image' && card.image && (
                      <ResponsiveImage document={card.image} />
                    )}

                    {card.cardType === 'video' && card.video && (
                      <Video
                        videoUrl={card.video.video.secureUrl}
                        videoType={card.video.videoType}
                        loopVideo={card.video.background?.loopVideo}
                        dialogueThumbnail={card.video.dialogue?.thumbnail}
                        includeControls={card.video.dialogue?.includeControls}
                        backupImage={card.video.backupImage}
                        ratios={card.video.ratios}
                        buttonType="primary"
                        classes="card-video"
                      />
                    )}
                  </div>
                  {(card.eyebrow || card.body || card.link?.text) && (
                    <div className="card-footer">
                      {card.eyebrow && (
                        <p className="type-label">{card.eyebrow}</p>
                      )}
                      {card.body && <p className="type-body">{card.body}</p>}
                      {card.link && (
                        <Link
                          link={card.link}
                          classes={['link link__no-margin type-label']}
                        >
                          {card.link.text}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </Section>
  )
}
